// The endpoint editor (milestone 8): a form over the endpoint spec (bucket,
// fields, filters, fixed filters, sort, paging, access, texts), a JSON tab
// for everything else, and a live preview that runs the draft through the
// gateway's own request code against the bucket (POST /endpoints/preview).
// Saving still creates a new version; publishing stays separate.
import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'

import { api, type BucketRow } from '../api'
import { ErrorBox, FindingsList, Pill } from '../components'
import { exampleQuery, normalizeSpec, opLabel, opsFor, paramType, type Column, type Fixed, type Param, type Sort, type Spec } from '../endpointSpec'

/** The bucket's output columns (from its current build spec). */
function useBucketColumns(slug: string) {
  const q = useQuery({ queryKey: ['bucket', slug], queryFn: () => api.bucket(slug), enabled: !!slug })
  const body = q.data?.spec?.body as { fields?: Column[]; primary_key?: string[] } | undefined
  return { columns: body?.fields ?? [], primaryKey: body?.primary_key ?? [], loading: q.isLoading }
}

interface FormProps {
  spec: Spec
  onChange: (s: Spec) => void
  readOnly: boolean
}

export function EndpointForm({ spec, onChange, readOnly }: FormProps) {
  const buckets = useQuery({ queryKey: ['buckets'], queryFn: api.buckets })
  const { columns, primaryKey, loading } = useBucketColumns(spec.bucket)
  const set = (patch: Spec) => onChange({ ...spec, ...patch })
  const params = (spec.params ?? []) as Param[]
  const fixed = (spec.fixed_filters ?? []) as Fixed[]
  const sort = (spec.sort ?? []) as Sort[]
  const fields = (spec.fields ?? []) as string[]
  const colType = (f: string) => columns.find((c) => c.name === f)?.type ?? 'VARCHAR'

  const pickBucket = (b: BucketRow | undefined) => {
    if (!b) return set({ bucket: '' })
    // A new bucket: start with every column returned and nothing filtered.
    set({ bucket: b.slug, fields: [], params: [], fixed_filters: [], sort: [] })
  }
  // Fill the fields once the chosen bucket's columns arrive.
  useEffect(() => {
    if (!readOnly && spec.bucket && columns.length && fields.length === 0) set({ fields: columns.map((c) => c.name) })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spec.bucket, columns.length])

  const paramFor = (field: string) => params.find((p) => p.field === field)
  const setParam = (field: string, p: Param | null) => {
    const rest = params.filter((x) => x.field !== field)
    set({ params: p ? [...params.filter((x) => x.field !== field), p].sort((a, b) => columns.findIndex((c) => c.name === a.field) - columns.findIndex((c) => c.name === b.field)) : rest })
  }
  const i18n = (k: 'summary' | 'description', lang: 'sv' | 'en', v: string) => {
    const cur = (spec[k] ?? {}) as { sv?: string; en?: string }
    const next = { sv: cur.sv ?? '', en: cur.en ?? '', [lang]: v }
    if (k === 'description' && !next.sv && !next.en) {
      const { description: _, ...rest } = spec // eslint-disable-line @typescript-eslint/no-unused-vars
      return onChange(rest)
    }
    set({ [k]: next })
  }

  return (
    <fieldset className="ep-form" disabled={readOnly}>
      <section>
        <h3>Route and data</h3>
        <div className="form-grid">
          <label>
            Path
            <input className="mono" value={spec.path ?? ''} placeholder="/v1/weather/now" onChange={(e) => set({ path: e.target.value })} />
            <span className="note">/v1/… with lower-case segments; {'{name}'} makes a path parameter.</span>
          </label>
          <label>
            Bucket
            <select value={spec.bucket ?? ''} onChange={(e) => pickBucket(buckets.data?.find((b) => b.slug === e.target.value))}>
              <option value="">Choose a bucket…</option>
              {buckets.data?.map((b) => (
                <option key={b.slug} value={b.slug}>
                  {b.slug} · {b.title}
                </option>
              ))}
            </select>
          </label>
          <label>
            Title (sv)
            <input value={spec.summary?.sv ?? ''} onChange={(e) => i18n('summary', 'sv', e.target.value)} />
          </label>
          <label>
            Title (en)
            <input value={spec.summary?.en ?? ''} onChange={(e) => i18n('summary', 'en', e.target.value)} />
          </label>
          <label className="wide">
            Description (sv, optional)
            <textarea rows={2} value={spec.description?.sv ?? ''} onChange={(e) => i18n('description', 'sv', e.target.value)} />
          </label>
          <label className="wide">
            Description (en, optional)
            <textarea rows={2} value={spec.description?.en ?? ''} onChange={(e) => i18n('description', 'en', e.target.value)} />
          </label>
        </div>
      </section>

      {spec.bucket && (
        <section>
          <h3>Fields and filters</h3>
          {loading && <p className="muted">Loading the bucket's columns…</p>}
          {!loading && columns.length === 0 && <p className="callout warn">The bucket has no build spec yet, so its columns are unknown.</p>}
          {columns.length > 0 && (
            <table className="tbl ep-cols">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      aria-label="Return every column"
                      checked={fields.length === columns.length}
                      onChange={(e) => set({ fields: e.target.checked ? columns.map((c) => c.name) : [] })}
                    />
                  </th>
                  <th>Column</th>
                  <th>Type</th>
                  <th>Callers can filter with</th>
                  <th>Required</th>
                  <th>Default</th>
                </tr>
              </thead>
              <tbody>
                {columns.map((c) => {
                  const p = paramFor(c.name)
                  const type = paramType(c.type)
                  const ops = p?.ops ?? (p ? ['eq'] : [])
                  const toggleOp = (op: string, on: boolean) => {
                    const next = on ? [...ops, op] : ops.filter((o) => o !== op)
                    if (next.length === 0) return setParam(c.name, null)
                    setParam(c.name, { ...(p ?? { name: c.name, field: c.name, type }), ops: opsFor[type].filter((o) => next.includes(o)) })
                  }
                  return (
                    <tr key={c.name}>
                      <td>
                        <input
                          type="checkbox"
                          aria-label={`Return ${c.name}`}
                          checked={fields.includes(c.name)}
                          onChange={(e) =>
                            set({ fields: e.target.checked ? columns.map((x) => x.name).filter((n) => n === c.name || fields.includes(n)) : fields.filter((f) => f !== c.name) })
                          }
                        />
                      </td>
                      <td>
                        <span className="cell-two">
                          <span className="mono">
                            {c.name}
                            {primaryKey.includes(c.name) && <Pill kind="neutral">key</Pill>}
                          </span>
                          {c.description && <span className="muted">{c.description}</span>}
                        </span>
                      </td>
                      <td className="mono">{c.type}</td>
                      <td>
                        <span className="ops">
                          {opsFor[type].map((op) => (
                            <label key={op} className="op" title={op === 'eq' ? `${c.name}=…` : `${c.name}.${op}=…`}>
                              <input type="checkbox" checked={ops.includes(op)} onChange={(e) => toggleOp(op, e.target.checked)} />
                              {opLabel[op]}
                            </label>
                          ))}
                        </span>
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          aria-label={`${c.name} required`}
                          disabled={!p}
                          checked={!!p?.required}
                          onChange={(e) => p && setParam(c.name, { ...p, required: e.target.checked || undefined })}
                        />
                      </td>
                      <td>
                        <input
                          className="mono small"
                          aria-label={`${c.name} default`}
                          disabled={!p}
                          value={p?.default === undefined ? '' : String(p.default)}
                          onChange={(e) => {
                            if (!p) return
                            const { default: _, ...rest } = p // eslint-disable-line @typescript-eslint/no-unused-vars
                            setParam(c.name, e.target.value === '' ? rest : { ...p, default: e.target.value })
                          }}
                        />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
          {params.some((p) => (spec.path ?? '').includes(`{${p.name}}`)) && (
            <p className="note">Parameters named in the path ({'{name}'}) are path parameters; the others are query parameters.</p>
          )}

          <h4>Always applied (callers cannot change these)</h4>
          {fixed.map((f, i) => {
            const type = paramType(colType(f.field))
            return (
              <div key={i} className="row">
                <select value={f.field} onChange={(e) => set({ fixed_filters: fixed.map((x, j) => (j === i ? { ...x, field: e.target.value } : x)) })}>
                  {columns.map((c) => (
                    <option key={c.name}>{c.name}</option>
                  ))}
                </select>
                <select value={f.op ?? 'eq'} onChange={(e) => set({ fixed_filters: fixed.map((x, j) => (j === i ? { ...x, op: e.target.value } : x)) })}>
                  {opsFor[type]
                    .filter((o) => o !== 'in')
                    .map((o) => (
                      <option key={o} value={o}>
                        {opLabel[o]}
                      </option>
                    ))}
                </select>
                <input
                  className="mono"
                  value={String(f.value ?? '')}
                  onChange={(e) =>
                    set({
                      fixed_filters: fixed.map((x, j) =>
                        j === i ? { ...x, value: type === 'boolean' ? e.target.value === 'true' : type === 'integer' || type === 'number' ? Number(e.target.value) : e.target.value } : x,
                      ),
                    })
                  }
                />
                <button type="button" className="btn btn-sm" onClick={() => set({ fixed_filters: fixed.filter((_, j) => j !== i) })}>
                  Remove
                </button>
              </div>
            )
          })}
          <button type="button" className="btn btn-sm" onClick={() => set({ fixed_filters: [...fixed, { field: columns[0]?.name ?? '', op: 'eq', value: '' }] })}>
            Add a fixed filter
          </button>

          <h4>Sort order</h4>
          {sort.map((s, i) => (
            <div key={i} className="row">
              <select value={s.field} onChange={(e) => set({ sort: sort.map((x, j) => (j === i ? { ...x, field: e.target.value } : x)) })}>
                {columns.map((c) => (
                  <option key={c.name}>{c.name}</option>
                ))}
              </select>
              <select value={s.dir ?? 'asc'} onChange={(e) => set({ sort: sort.map((x, j) => (j === i ? { ...x, dir: e.target.value as 'asc' | 'desc' } : x)) })}>
                <option value="asc">ascending</option>
                <option value="desc">descending</option>
              </select>
              <button type="button" className="btn btn-sm" disabled={i === 0} onClick={() => set({ sort: sort.map((x, j) => (j === i - 1 ? sort[i] : j === i ? sort[i - 1] : x)) })}>
                ↑
              </button>
              <button type="button" className="btn btn-sm" onClick={() => set({ sort: sort.filter((_, j) => j !== i) })}>
                Remove
              </button>
            </div>
          ))}
          <button type="button" className="btn btn-sm" onClick={() => set({ sort: [...sort, { field: columns[0]?.name ?? '' }] })}>
            Add a sort column
          </button>
          <p className="note">The bucket's primary key is always added last, so paging is stable.</p>
        </section>
      )}

      <section>
        <h3>Response and access</h3>
        <div className="form-grid">
          <label className="check">
            <input type="checkbox" checked={!!spec.single} onChange={(e) => set({ single: e.target.checked || undefined })} /> One object (404 when there is no match)
            instead of a list
          </label>
          {!spec.single && (
            <>
              <label>
                Paging
                <select value={spec.pagination?.mode ?? 'cursor'} onChange={(e) => set({ pagination: { ...spec.pagination, mode: e.target.value } })}>
                  <option value="cursor">cursor (next_cursor)</option>
                  <option value="none" disabled={!spec.bulk}>none (one page; bulk endpoints only)</option>
                </select>
              </label>
              <label>
                Rows by default
                <input type="number" min={1} max={10000} value={spec.pagination?.default_limit ?? 100}
                  onChange={(e) => set({ pagination: { ...spec.pagination, default_limit: Number(e.target.value) } })} />
              </label>
              <label>
                At most (limit=)
                <input type="number" min={1} max={10000} value={spec.pagination?.max_limit ?? 1000}
                  onChange={(e) => set({ pagination: { ...spec.pagination, max_limit: Number(e.target.value) } })} />
                <span className="note">Above 1000 only for bulk endpoints.</span>
              </label>
            </>
          )}
          <label>
            Cost per request
            <select value={spec.tier ?? 'standard'} onChange={(e) => set({ tier: e.target.value })}>
              <option value="light">light, 1 unit</option>
              <option value="standard">standard, 2 units</option>
              <option value="heavy">heavy, 5 units</option>
            </select>
          </label>
          <label>
            API key
            <select value={spec.auth ?? 'key_optional'} onChange={(e) => set({ auth: e.target.value })}>
              <option value="anonymous">not used</option>
              <option value="key_optional">optional (free tier without)</option>
              <option value="key_required">required</option>
            </select>
          </label>
          <label>
            Cache (seconds, optional)
            <input type="number" min={0} value={spec.cache_seconds ?? ''}
              onChange={(e) => {
                const { cache_seconds: _, ...rest } = spec // eslint-disable-line @typescript-eslint/no-unused-vars
                onChange(e.target.value === '' ? rest : { ...spec, cache_seconds: Number(e.target.value) })
              }} />
          </label>
          <label className="check">
            <input type="checkbox" checked={!!spec.bulk} onChange={(e) => set({ bulk: e.target.checked || undefined })} /> Bulk export (only over an exportable
            bucket)
          </label>
        </div>
      </section>
    </fieldset>
  )
}

/** Runs the draft through the gateway's request code against the bucket. */
export function EndpointPreview({ slug, spec }: { slug?: string; spec: Spec }) {
  const pathParams = useMemo(() => [...String(spec.path ?? '').matchAll(/\{([a-z_][a-z0-9_]*)\}/g)].map((m) => m[1]), [spec.path])
  const [query, setQuery] = useState(() => exampleQuery(spec))
  const [path, setPath] = useState<Record<string, string>>({})
  const run = useMutation({ mutationFn: () => api.previewEndpoint({ slug, spec: normalizeSpec(spec), query, path }) })
  const r = run.data
  const rows = (r?.data ?? []) as Record<string, unknown>[]
  const cols = rows.length ? Object.keys(rows[0]) : []
  return (
    <div className="card">
      <h2>Preview</h2>
      <p className="note">
        Runs this draft the way the API would, against the bucket's data, without saving. Parameters: <code>name</code> for equals,{' '}
        <code>name.op</code> otherwise (<code>date.gte=2026-10-01</code>, <code>tld.in=se,nu</code>).
      </p>
      <form
        className="row"
        onSubmit={(e) => {
          e.preventDefault()
          run.mutate()
        }}
      >
        <span className="mono muted">GET {spec.path}?</span>
        {pathParams.map((p) => (
          <input key={p} className="mono" placeholder={p} aria-label={`Path parameter ${p}`} value={path[p] ?? ''} onChange={(e) => setPath({ ...path, [p]: e.target.value })} />
        ))}
        <input className="mono grow" aria-label="Query string" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="tld=se&limit=10" />
        <button className="btn btn-primary" type="submit" disabled={run.isPending || !spec.bucket}>
          {run.isPending ? 'Running…' : 'Run'}
        </button>
      </form>
      {run.error && <ErrorBox error={run.error} />}
      {r && (
        <div className="report">
          {r.gate.problems.map((p, i) => (
            <p key={i} className="callout err">
              <strong>Problem</strong> {p}
            </p>
          ))}
          {r.request && (
            <p>
              <code>{r.request}</code>{' '}
              {r.status === 200 ? <Pill kind="ok">200</Pill> : r.status ? <Pill kind="err">{r.status}</Pill> : null} {r.error && <span className="err-t">{r.error}</span>}
            </p>
          )}
          {r.status === 200 && (
            <>
              <p className="muted">
                {rows.length} row{rows.length === 1 ? '' : 's'}
                {r.more ? ' shown; there are more (next_cursor)' : ''}.
              </p>
              {rows.length > 0 && (
                <div className="tbl-scroll">
                  <table className="tbl compact">
                    <thead>
                      <tr>{cols.map((c) => <th key={c} className="mono">{c}</th>)}</tr>
                    </thead>
                    <tbody>
                      {rows.map((row, i) => (
                        <tr key={i}>{cols.map((c) => <td key={c} className="mono">{fmtCell(row[c])}</td>)}</tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
          <FindingsList findings={r.gate.findings} />
          {r.gate.problems.length === 0 && (
            <p className={r.gate.ok ? 'callout ok' : 'callout warn'}>{r.gate.ok ? 'This version can be published.' : 'The licence findings above block publishing this version.'}</p>
          )}
          {r.sql && (
            <details>
              <summary>SQL</summary>
              <code className="block spec">{r.sql}</code>
            </details>
          )}
        </div>
      )}
    </div>
  )
}

function fmtCell(v: unknown): string {
  if (v == null) return '–'
  if (typeof v === 'object') return JSON.stringify(v)
  return String(v)
}
